import { bagMechanism, confirmedMonsterIds, groupLabel, itemCoverage, monsterItemDrops, playerItemSources, qualityLabel, resolveItemSources, routeDisabled, routeQuality, sourceGroup, sourceSummary, targetContext } from './itemSources'
import { allBagSources, bagContents, bagsOpenedBy, isOpenableBag, monsterBags, playerBagSources } from './bagSources'
import { targetWithheld } from './invasionValidation'
import { searchGuide } from './guideSearch'
import { detectIntent } from './searchIntents'
import type { SearchDoc } from './guideSearch'
import type { ItemCoverage, MonsterTarget, ResolvedSource } from './itemSources'
import { coordLabel, mapName, monsterName, itemName } from './worldFormat'
import { monsterDrops } from './worldQueries'
import { normalize } from './text'
import type { WorldData } from '../types/world'

/*
 * Diagnóstico copiable calculado en el navegador con los archivos realmente cargados.
 * Separa SIEMPRE cuatro niveles:
 *   1. Registros originales en los archivos (antes de cualquier filtro).
 *   2. Rutas desactivadas por configuración (verified_possible = False o tasas en cero).
 *   3. Rutas/objetivos retenidos por revisión (invasiones sin aprobación o en conflicto).
 *   4. Resultados visibles para el jugador.
 */

export type ItemExample = { label: string; query: string }
export const itemExamples: ItemExample[] = [
  { label: 'Armadura (7,1)', query: 'Dragon Helm' },
  { label: 'Poder', query: 'Evil Spirit' },
  { label: 'Arma', query: 'Kris' },
  { label: 'Joya', query: 'Jewel of Chaos' },
  { label: 'Caja (variantes de 14,11)', query: 'Box of Kundun' },
]
export const monsterExample = 'Bull Fighter'

type Found = { key: string; name: string; isVariant: boolean }

/** Busca por nombre en ítems Y variantes; una variante se identifica por su id y su ítem base. */
function findItems(w: WorldData, q: string): Found[] {
  const nq = normalize(q)
  const all: Found[] = [
    ...Array.from(w.items.values()).map((i) => ({ key: i.id, name: i.name, isVariant: false })),
    ...Array.from(w.variants.values()).filter((v) => v.id !== v.itemId).map((v) => ({ key: v.id, name: v.name, isVariant: true })),
  ]
  const exact = all.filter((f) => normalize(f.name) === nq)
  if (exact.length) return exact
  return all.filter((f) => normalize(f.name).includes(nq)).sort((a, b) => a.key.localeCompare(b.key, 'es', { numeric: true })).slice(0, 10)
}

function targetLine(w: WorldData, t: MonsterTarget, indent: string, tag = '') {
  const spots = t.spawns.length ? t.spawns.slice(0, 2).map((sp) => `${mapName(w, sp.mapId)} ${coordLabel(sp) ?? 'sin coordenadas'}`).join(' | ') : 'ubicación exacta no documentada'
  return `${indent}    · ${tag}${monsterName(w, t.monsterId)} (id ${t.monsterId}) · ${t.mapId !== undefined ? mapName(w, t.mapId) : 'mapa —'} · ${targetContext(t) === 'event' ? `monstruo dentro de evento${t.eventId ? ` ${t.eventId}` : ''}` : 'drop ordinario'}${t.isIndirect ? ' · INDIRECTO' : ''} · ${spots}`
}

function sourceLines(w: WorldData, list: ResolvedSource[], indent = ''): string[] {
  const out: string[] = []
  list.forEach((s) => {
    const state = s.blocked ? 'EN REVISIÓN (retenida, no publicada)' : s.status === 'disabled' ? 'DESACTIVADA' : s.pending ? 'FALTA INFORMACIÓN' : 'VISIBLE'
    out.push(`${indent}- [${state}] ${s.kind} · ${qualityLabel[routeQuality(s.route, w)]} · ${s.label ?? ''} · ${s.route.id}${s.mechanism ? ` · mecanismo ${s.mechanism === 'internal' ? 'recompensa interna' : 'caja'}` : ''}`)
    out.push(`${indent}    Objetivos originales ${s.rawTargetRows} · retenidos ${s.heldTargetRows} · visibles ${s.targets.length} monstruo/mapa${s.eventMaps.length ? ` + ${s.eventMaps.length} mapas de premio` : ''}`)
    if (s.pendingReason) out.push(`${indent}    MOTIVO: ${s.pendingReason}`)
    if (s.conditions) out.push(`${indent}    Condiciones: ${s.conditions}`)
    s.techNotes.forEach((n) => out.push(`${indent}    ${n}`))
    s.targets.slice(0, 8).forEach((t) => out.push(targetLine(w, t, indent)))
    if (s.targets.length > 8) out.push(`${indent}    · … ${s.targets.length - 8} más`)
    s.heldTargets.slice(0, 6).forEach((t) => out.push(targetLine(w, t, indent, 'RETENIDO · ')))
    if (s.heldTargets.length > 6) out.push(`${indent}    · … ${s.heldTargets.length - 6} retenidos más`)
    s.via?.forEach((v) => {
      out.push(`${indent}    Caja intermedia: ${v.name} (${v.itemId})`)
      out.push(...sourceLines(w, v.sources, `${indent}      `))
    })
  })
  return out
}

type Verdict = 'confirmed' | 'review' | 'pending' | 'disabled' | 'no-routes' | 'not-found'
const verdictText: Record<Verdict, string> = {
  confirmed: 'VISIBLE: forma de obtención documentada y publicada',
  review: 'EN REVISIÓN: datos documentados, publicación retenida',
  pending: 'FALTA INFORMACIÓN: rutas activas sin objetivos',
  disabled: 'TODAS LAS RUTAS DESACTIVADAS',
  'no-routes': 'SIN RUTAS en 10_drop_routes',
  'not-found': 'NO ENCONTRADO en 01_items ni 02_item_variants',
}

function itemVerdict(sources: ResolvedSource[]): Verdict {
  if (!sources.length) return 'no-routes'
  if (confirmedMonsterIds(sources).length) return 'confirmed'
  if (sources.some((s) => s.status !== 'disabled' && !s.pending && !s.blocked)) return 'confirmed'
  if (sources.some((s) => s.blocked)) return 'review'
  if (sources.every((s) => s.status === 'disabled')) return 'disabled'
  return 'pending'
}

/** Recuento por niveles para un ítem o variante. */
function layerLines(w: WorldData, key: string, sources: ResolvedSource[]): string[] {
  const rawRows = sources.reduce((n, s) => n + s.rawTargetRows, 0)
  const held = sources.filter((s) => s.blocked)
  const disabled = sources.filter((s) => !s.blocked && s.status === 'disabled')
  const shown = playerItemSources(w, key).filter((s) => s.status !== 'disabled')
  const counts = new Map<string, number>()
  shown.forEach((s) => {
    const k = `${groupLabel[sourceGroup(s)]} · ${s.label} · ${qualityLabel[routeQuality(s.route, w)]}`
    counts.set(k, (counts.get(k) ?? 0) + 1)
  })
  const bagsAll = allBagSources(w, key)
  const bagsShown = playerBagSources(w, key)
  return [
    `1. ORIGINAL: ${sources.length} rutas · ${rawRows} filas de objetivos en route_targets`,
    `2. DESACTIVADAS por configuración: ${disabled.length}`,
    `3. EN REVISIÓN (retenidas): ${held.length} rutas · ${sources.reduce((n, s) => n + s.heldTargetRows, 0)} filas de objetivos retenidas`,
    `4. VISIBLE para el jugador: ${shown.length} entradas${counts.size ? ` → ${Array.from(counts.entries()).map(([k, n]) => `${k}: ${n}`).join(' | ')}` : ''} · con falta de información ${shown.filter((s) => s.pending).length}`,
    `   Bolsas que lo contienen: ${bagsAll.length} originales · ${bagsAll.filter((b) => b.withheldReason).length} en revisión · ${bagsShown.length} visibles (${bagsShown.filter(isOpenableBag).length} cajas que se abren, ${bagsShown.filter((b) => !isOpenableBag(b)).length} recompensas internas)`,
  ]
}

function itemBlock(w: WorldData, ex: ItemExample): { lines: string[]; verdict: Verdict } {
  const found = findItems(w, ex.query)
  if (!found.length) return { lines: [`## ${ex.label}: "${ex.query}" → ${verdictText['not-found']}`], verdict: 'not-found' }
  const lines: string[] = [`## ${ex.label}: "${ex.query}" → ${found.length} coincidencia(s)`]
  let best: Verdict = 'not-found'
  const rank: Verdict[] = ['confirmed', 'review', 'pending', 'disabled', 'no-routes', 'not-found']
  found.forEach((f) => {
    const sources = resolveItemSources(w, f.key)
    const v = itemVerdict(sources)
    if (rank.indexOf(v) < rank.indexOf(best)) best = v
    const variant = w.variants.get(f.key)
    const base = variant ? w.items.get(variant.itemId) : w.items.get(f.key)
    const it = base
    const sum = sourceSummary(sources)
    const mons = confirmedMonsterIds(sources)
    lines.push(
      '',
      f.isVariant
        ? `VARIANTE: ${f.name} · id ${f.key} · ítem base ${variant!.itemId} (${base?.name ?? 'no está en 01_items'}) · nivel ${variant!.level ?? '—'} · cajas que abre: ${bagsOpenedBy(w, f.key).map((b) => b.id).join(', ') || '—'}`
        : `ÍTEM: ${f.name} · id ${f.key} · sección ${it?.section ?? '—'} · índice ${it?.index ?? '—'} · skill_id ${it?.skillId ?? '—'} · drop_level ${it?.dropLevel ?? '—'}`,
      `VEREDICTO: ${verdictText[v]}`,
      `RUTAS: ${sum.total} · activas ${sum.active} · solo ciertas cuentas ${sum.partial} · desactivadas ${sum.disabled}`,
      `MONSTRUOS VISIBLES: ${mons.length}${mons.length ? ` → ${mons.slice(0, 15).map((id) => monsterName(w, id)).join(', ')}${mons.length > 15 ? '…' : ''}` : ''}`,
      ...layerLines(w, f.key, sources),
      ...sourceLines(w, sources),
    )
    if (!f.isVariant) {
      ;(w.idx.variantsByItem.get(f.key) ?? [])
        .filter((x) => x.id !== f.key)
        .forEach((x) => {
          const vs = resolveItemSources(w, x.id)
          lines.push(`  VARIANTE ${x.name} (${x.id}): ${verdictText[itemVerdict(vs)]} · rutas ${vs.length} · objetivos originales ${vs.reduce((n, s) => n + s.rawTargetRows, 0)}`)
        })
    }
  })
  return { lines, verdict: best }
}

const searchChecks = ['casco dragón', 'alas', 'alas evo 3', 'quest Marlon', 'cómo entro a Barracks', 'Evil Spirit', 'Bull Fighter', 'pluma de condor', 'Box of Kundun']

function searchBlock(docs: SearchDoc[]): string[] {
  if (!docs.length) return ['## BUSCADOR: índice de datos no disponible (no comprobado)']
  return [
    '## BUSCADOR (mismo índice y prioridades que usa la guía)',
    ...searchChecks.map((q) => {
      const intent = detectIntent(q)
      const r = searchGuide(q, docs).flatMap((g) => g.results).sort((a, b) => b.score - a.score).slice(0, 3)
      return `- "${q}" → ${intent ? `[atajo: ${intent.options[0].label} ${intent.options[0].to}] ` : ''}${r.length ? r.map((x) => `${x.doc.title} [${x.doc.group}] ${x.doc.path}`).join(' | ') : 'SIN RESULTADOS'}`
    }),
  ]
}

function monsterBlock(w: WorldData, q: string): string[] {
  const nq = normalize(q)
  const m = Array.from(w.monsters.values()).find((x) => normalize(x.name) === nq) ?? Array.from(w.monsters.values()).find((x) => normalize(x.name).includes(nq))
  if (!m) return [`## Monstruo: "${q}" → NO ENCONTRADO en 06_monsters`]
  const locs = w.idx.locationsByMonster.get(m.id) ?? []
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? []
  const drops = monsterDrops(w, m.id)
  const rows = w.idx.routeTargetsByMonster.get(m.id) ?? []
  // Niveles calculados sobre las filas ORIGINALES.
  const rawRoutes = new Set<string>()
  const rawItems = new Set<string>()
  const heldItems = new Set<string>()
  const disabledItems = new Set<string>()
  const ctx: Record<string, number> = {}
  let heldRows = 0
  rows.forEach((t) => {
    const r = w.idx.routeById.get(t.routeId)
    if (!r) return
    rawRoutes.add(r.id)
    const key = r.variantId && w.variants.has(r.variantId) ? r.variantId : r.itemId ?? '—'
    rawItems.add(key)
    const c = targetContext(t)
    ctx[c] = (ctx[c] ?? 0) + 1
    if (targetWithheld(w, r, t)) {
      heldRows++
      heldItems.add(key)
    } else if (routeDisabled(w, r)) disabledItems.add(key)
  })
  const visible = monsterItemDrops(w, m.id).filter((i) => !i.disabled)
  const direct = visible.filter((i) => !i.indirect)
  return [
    `## Monstruo: "${q}" → ${m.name} · id ${m.id} · nivel ${m.level ?? '—'}`,
    `monster_locations: ${locs.length}${locs.length ? ` → ${Array.from(new Set(locs.map((l) => `${mapName(w, l.mapId)} [${l.kind}]`))).join(', ')}` : ' → Ubicación no documentada'}`,
    `spawns: ${spawns.length}${spawns.length ? ` → ${spawns.slice(0, 5).map((s) => `${mapName(w, s.mapId)} ${coordLabel(s) ?? 'sin coordenadas'} (${s.eventId ?? s.kind})`).join(' | ')}${spawns.length > 5 ? ' …' : ''}` : ''}`,
    `1. ORIGINAL: ${rows.length} filas en route_targets · ${rawRoutes.size} rutas · ${rawItems.size} ítems/variantes · contexto ${Object.entries(ctx).map(([k, n]) => `${k}=${n}`).join(', ') || '—'}`,
    `2. DESACTIVADAS: ${disabledItems.size} ítems solo con rutas desactivadas`,
    `3. EN REVISIÓN: ${heldRows} filas · ${heldItems.size} ítems retenidos (asociaciones existentes, publicación pendiente)`,
    `4. VISIBLE: ${visible.length} ítems (${direct.length} directos, ${visible.length - direct.length} solo indirectos)`,
    `   Ejemplos visibles: ${direct.slice(0, 15).map((i) => i.name).join(', ') || '—'}`,
    `   Ejemplos en revisión: ${Array.from(heldItems).slice(0, 15).map((k) => itemName(w, k)).join(', ') || '—'}`,
    `Reglas especiales (item_drop_rule_targets / monster_filter): ${drops.rules.length}`,
    `Bolsas al derrotarlo (17/34): ${monsterBags(w, m.id).map((b) => {
      const c = bagContents(w, b.id)
      return `${b.id} → ${c.length} resultados posibles, ${c.filter((x) => x.withheld).length} en revisión`
    }).join(' · ') || 'ninguna'}`,
    `VEREDICTO: ${spawns.length || locs.length ? 'ubicación documentada' : 'ubicación no documentada'} · ${visible.length ? `${visible.length} ítems visibles` : heldItems.size ? `${heldItems.size} ítems documentados EN REVISIÓN, 0 publicados` : rows.length ? 'solo rutas desactivadas' : 'sin objetivos en route_targets'}`,
  ]
}

const coverageLabel: Record<ItemCoverage, string> = {
  documented: 'obtención documentada (monstruo + mapa + coordenadas, o premio de evento)',
  partial: 'información parcial (falta un dato concreto)',
  review: 'en revisión (datos presentes, publicación retenida)',
  'all-disabled': 'todas sus rutas desactivadas',
  'no-routes': 'sin rutas',
}

function globalBlock(w: WorldData): { lines: string[]; affected: string[] } {
  const counts: Record<ItemCoverage, number> = { documented: 0, partial: 0, review: 0, 'all-disabled': 0, 'no-routes': 0 }
  const affected: string[] = []
  const reasons = new Map<string, number>()
  w.items.forEach((it) => {
    const sources = resolveItemSources(w, it.id)
    const c = itemCoverage(sources)
    counts[c.coverage]++
    if (c.coverage !== 'documented') {
      affected.push(`${it.id}\t${it.name}\t${coverageLabel[c.coverage]}\t${c.reason ?? '—'}`)
      const k = `${coverageLabel[c.coverage]} — ${(c.reason ?? '—').replace(/route:\w+|rule:\d+|\d+,\d+(@\d+)?|\d+ objetivos|drop_level del ítem [\d—]+/g, '…')}`
      reasons.set(k, (reasons.get(k) ?? 0) + 1)
    }
  })
  const pub = w.publication
  const exNoRoutes = pub.excludedItems.filter((e) => e.routes === 0).length
  w.publication.excludedItems.forEach((e) => affected.push(`${e.id}\t${e.name}\tEXCLUIDO POR FILTRO DE PUBLICACIÓN\t${e.reason} · ${e.routes} rutas originales`))

  const routesByKind = w.routes.reduce<Record<string, number>>((a, r) => ((a[r.kind] = (a[r.kind] ?? 0) + 1), a), {})
  const naturalRoutes = w.routes.filter((r) => r.kind === 'natural')
  const naturalWithTargets = naturalRoutes.filter((r) => (w.idx.routeTargetsByRoute.get(r.id)?.length ?? 0) > 0)
  const rtBadRoute = w.routeTargets.filter((t) => !w.idx.routeById.has(t.routeId)).length
  const rtBadMonster = w.routeTargets.filter((t) => t.monsterId !== undefined && !w.monsters.has(t.monsterId)).length
  const rtBadMap = w.routeTargets.filter((t) => t.mapId !== undefined && !w.maps.has(t.mapId)).length
  const rtKinds = w.routeTargets.reduce<Record<string, number>>((a, t) => ((a[t.locationKind ?? '—'] = (a[t.locationKind ?? '—'] ?? 0) + 1), a), {})
  const heldRows = w.routeTargets.filter((t) => {
    const r = w.idx.routeById.get(t.routeId)
    return r ? targetWithheld(w, r, t) : false
  }).length
  const disabledRoutes = w.routes.filter((r) => routeDisabled(w, r)).length
  const mixedRoutes = w.routes.filter((r) => {
    const c = new Set((w.idx.routeTargetsByRoute.get(r.id) ?? []).filter((t) => t.monsterId !== undefined).map(targetContext))
    return c.size > 1
  }).length
  const brokenRuleRefs = w.routes.filter((r) => r.ruleId && !w.rules.has(r.ruleId)).length
  const brokenVia = w.routes.filter((r) => r.viaItemIds.some((id) => !w.items.has(id) && !w.variants.has(id))).length
  const bagMech = { box: 0, internal: 0, missing: 0 }
  w.routes.filter((r) => r.kind === 'bag').forEach((r) => {
    const m = bagMechanism(w, r, (w.idx.routeTargetsByRoute.get(r.id)?.length ?? 0) > 0).mechanism
    bagMech[m ?? 'missing']++
  })
  const variantsNoBase = Array.from(w.variants.values()).filter((v) => !w.items.has(v.itemId)).length

  return {
    affected,
    lines: [
      '## CATÁLOGO (01_items + 02_item_variants)',
      `Filas originales en 01_items: ${pub.rawItems} · publicadas: ${w.items.size} · excluidas por filtro de publicación: ${pub.excludedItems.length}`,
      ...(pub.excludedItems.length
        ? [`  Excluidas (${Array.from(new Set(pub.excludedItems.map((e) => e.reason))).join(' / ')}): ${pub.excludedItems.map((e) => e.id).join(', ')}`, `  Con el filtro se ocultan también ${pub.excludedVariants} variantes, ${pub.excludedRoutes} rutas y ${pub.excludedRouteTargets} filas de objetivos.`]
        : []),
      `Variantes cargadas: ${w.variants.size} · sin ítem base en 01_items: ${variantsNoBase}`,
      `Ítems sin ninguna ruta (cálculo sobre los datos): ${counts['no-routes']} publicados + ${exNoRoutes} excluidos = ${counts['no-routes'] + exNoRoutes} de ${pub.rawItems}`,
      '',
      '## ÍTEMS PUBLICADOS POR ESTADO',
      ...(Object.keys(counts) as ItemCoverage[]).map((k) => `- ${coverageLabel[k]}: ${counts[k]}`),
      '',
      '## CONTEOS ORIGINALES / TRAS EL FILTRO DE PUBLICACIÓN (calculados al cargar)',
      `Rutas: ${pub.rawRoutes} originales / ${w.routes.length} tras el filtro (−${pub.rawRoutes - w.routes.length})`,
      `Objetivos (route_targets): ${pub.rawRouteTargets} originales / ${w.routeTargets.length} tras el filtro (−${pub.rawRouteTargets - w.routeTargets.length})`,
      `Variantes: ${pub.rawVariants} originales / ${w.variants.size} tras el filtro (−${pub.rawVariants - w.variants.size})`,
      `Rutas naturales: ${pub.rawNaturalRoutes} originales / ${naturalRoutes.length} tras el filtro (−${pub.rawNaturalRoutes - naturalRoutes.length})`,
      '',
      '## RUTAS Y OBJETIVOS PUBLICADOS (tras el filtro de publicación)',
      `Rutas: ${w.routes.length} · por tipo: ${Object.entries(routesByKind).map(([k, n]) => `${k}=${n}`).join(', ')}`,
      `Rutas desactivadas por configuración: ${disabledRoutes} · rutas natural con objetivos: ${naturalWithTargets.length} de ${naturalRoutes.length}`,
      `route_targets: ${w.routeTargets.length} filas · retenidas por revisión: ${heldRows} · location_kind: ${Object.entries(rtKinds).map(([k, n]) => `${k}=${n}`).join(', ')}`,
      `Rutas con objetivos en contextos mezclados (mapa normal + evento): ${mixedRoutes} → se muestran por separado`,
      `Rutas bag por mecanismo: caja que se abre ${bagMech.box} · recompensa interna al derrotar ${bagMech.internal} · sin mecanismo determinable ${bagMech.missing}`,
      '',
      `RELACIONES ROTAS → ruta inexistente ${rtBadRoute} · monstruo inexistente ${rtBadMonster} · mapa inexistente ${rtBadMap} · rule_id inexistente ${brokenRuleRefs} · via_item_ids inexistente ${brokenVia} · bolsa sin mecanismo ${bagMech.missing}`,
      '',
      `MOTIVOS DE LOS ÍTEMS NO DOCUMENTADOS (lista completa en "Copiar ítems pendientes"): ${reasons.size ? '' : 'ninguno'}`,
      ...Array.from(reasons.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([r, n]) => `- ${n} × ${r}`),
    ],
  }
}

export function buildDiagnostic(w: WorldData, docs: SearchDoc[] = []) {
  const loadErrors = w.reports.filter((r) => !r.loaded || r.parseErrors.length)
  const examples = itemExamples.map((ex) => ({ ex, ...itemBlock(w, ex) }))
  const global = globalBlock(w)
  const lines = [
    'DIAGNÓSTICO MU VANGUARD — guía interactiva',
    `Calculado: ${new Date().toLocaleString('es-ES')} en este navegador`,
    'Niveles: 1 ORIGINAL (archivos) · 2 DESACTIVADO (configuración) · 3 EN REVISIÓN (retenido) · 4 VISIBLE (jugador)',
    '',
    '## ARCHIVOS',
    ...w.reports.map((r) => `- ${r.label}: ${r.loaded ? `cargado, ${r.rows} filas` : 'NO CARGADO'}${r.parseErrors.length ? `, ${r.parseErrors.length} errores de lectura` : ''}${r.duplicateCount ? `, ${r.duplicateCount} IDs duplicados` : ''}`),
    '',
    ...(loadErrors.length ? ['ERRORES DE CARGA:', ...loadErrors.flatMap((r) => [`- ${r.label}${r.loaded ? '' : ': no se pudo descargar'}`, ...r.parseErrors.slice(0, 8).map((e) => `    ${e}`)])] : ['ERRORES DE CARGA: ninguno']),
    '',
    ...global.lines,
    '',
    ...examples.flatMap((e) => [...e.lines, '']),
    ...monsterBlock(w, monsterExample),
    '',
    ...monsterBlock(w, 'Red Dragon'),
    '',
    ...searchBlock(docs),
  ]
  return {
    report: lines.join('\n'),
    affectedList: ['item_id\tnombre\testado\tmotivo', ...global.affected].join('\n'),
    affectedCount: global.affected.length,
    loadErrors,
    examples: examples.map((e) => ({ label: e.ex.label, query: e.ex.query, verdict: e.verdict })),
  }
}
